import * as React from 'react';
import ReactDOM from 'react-dom/client';
import App from '@/App';
import { BrowserRouter as Router } from 'react-router-dom';
import { Config, ConfigProvider } from '@/config';
import { AuthProvider } from '@/providers/AuthProvider';
import { initCoreApi } from '@/apis/core';
import { Amplify } from 'aws-amplify';
import { MuiThemeProvider } from '@/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@/styles/app.css';
import '@/styles/tailwind.css';
import { HelmetProvider } from 'react-helmet-async';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';

const onLoad = (config: Config) => {
  initCoreApi(config.omiseCraftCoreUrl);
  if (config.siteMode == 'operator' && Object.keys(config.amplify.operator ?? {}).length) {
    // isLocalOperatorSite
    // ローカルでは、operatorにoperatorのamplify値を格納しておく。
    Amplify.configure(config.amplify.operator);
  } else {
    // aws 上では、01にはdefaultへmemberのamplify値が、
    // 02にはdefaultへoperatorのamplify値が、omise-craft-infraにて、セットされる。
    Amplify.configure(config.amplify.default);
  }

  try {
    // Initialize CloudWatch RUM web client
    const awsRumConfig: AwsRumConfig = {
      sessionSampleRate: 1,
      identityPoolId: config.cloudwatchRum.identityPoolId,
      endpoint: `https://dataplane.rum.${config.cloudwatchRum.region}.amazonaws.com`,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: true, // Enable X-Ray tracing
      signing: true // If you have a public resource policy and wish to send unsigned requests please set this to false
    };

    const APPLICATION_ID: string = config.cloudwatchRum.applicationId;
    const APPLICATION_VERSION: string = config.version;
    const APPLICATION_REGION: string = config.cloudwatchRum.region;
    console.log('Initializing AWS RUM with config:', {
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION
    });

    new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, awsRumConfig);
  } catch (error) {
    console.error('Failed to initialize AWS RUM:', error);
  }
};

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <ConfigProvider onLoad={onLoad}>
      <QueryClientProvider client={queryClient}>
        <MuiThemeProvider>
          <HelmetProvider>
            <Router>
              <AuthProvider>
                <App />
              </AuthProvider>
            </Router>
          </HelmetProvider>
        </MuiThemeProvider>
      </QueryClientProvider>
    </ConfigProvider>
  </React.StrictMode>
);
